import React, { useEffect, useState, useMemo } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "../lib/AxiosGo";
import { toast } from "react-toastify";

const ReportPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCrime, setFilterCrime] = useState("all");
  const [filterEvidence, setFilterEvidence] = useState("all");
  const [selectedReport, setSelectedReport] = useState(null);

  const getData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/reports/data");
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching report data:", error);
      toast.error("Gagal mengambil data laporan pengaduan");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus data laporan ini? Tindakan ini tidak dapat dibatalkan.")) return;
    try {
      await axios.delete(`/reports/${reportId}`);
      toast.success("Laporan berhasil dihapus!");
      if (selectedReport && (selectedReport.id === reportId || selectedReport.report_id === reportId)) {
        setSelectedReport(null);
      }
      getData();
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Gagal menghapus laporan");
    }
  };

  const handleDownload = (filePath) => {
    if (!filePath) return;
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/";
    const base = apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;
    window.open(`${base}reports/attachment?path=${encodeURIComponent(filePath)}`, "_blank");
  };

  const handleDownloadLetter = (reportId) => {
    if (!reportId) return;
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/";
    const base = apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;
    window.open(`${base}reports/export-pdf?reportId=${reportId}`, "_blank");
  };

  useEffect(() => {
    getData();
  }, []);

  // Filtered reports
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const name = (item.reporter?.full_name || "").toLowerCase();
      const phone = (item.reporter?.phone || item.reporter?.no_wa || "").toLowerCase();
      const crime = (item.report_type_of_crime || "").toLowerCase();
      const location = (item.report_location || "").toLowerCase();
      const caseText = (item.report_case || "").toLowerCase();
      const victim = (item.report_victim_name || "").toLowerCase();
      const q = searchTerm.toLowerCase().trim();

      const matchSearch =
        !q ||
        name.includes(q) ||
        phone.includes(q) ||
        crime.includes(q) ||
        location.includes(q) ||
        caseText.includes(q) ||
        victim.includes(q);

      const matchCrime = filterCrime === "all" || (item.report_type_of_crime || "").toLowerCase().includes(filterCrime.toLowerCase());

      const matchEvidence =
        filterEvidence === "all" ||
        (filterEvidence === "yes" && !!item.report_attachment) ||
        (filterEvidence === "no" && !item.report_attachment);

      return matchSearch && matchCrime && matchEvidence;
    });
  }, [data, searchTerm, filterCrime, filterEvidence]);

  // Statistics calculation
  const stats = useMemo(() => {
    const total = data.length;
    const agreed = data.filter((d) => d.report_agreement_reporter).length;
    const withEvidence = data.filter((d) => !!d.report_attachment).length;

    const todayStr = new Date().toISOString().slice(0, 10);
    const todayCount = data.filter((d) => d.createdAt && d.createdAt.slice(0, 10) === todayStr).length;

    return { total, agreed, withEvidence, todayCount };
  }, [data]);

  const getCrimeBadgeClass = (crimeType) => {
    const t = (crimeType || "").toLowerCase();
    if (t.includes("anak")) return "bg-danger-subtle text-danger-main border border-danger-subtle";
    if (t.includes("kdrt") || t.includes("rumah tangga")) return "bg-warning-subtle text-warning-main border border-warning-subtle";
    if (t.includes("seksual")) return "bg-purple-subtle text-purple-main border border-purple-subtle";
    if (t.includes("tppo") || t.includes("perdagangan")) return "bg-info-subtle text-info-main border border-info-subtle";
    if (t.includes("ekonomi") || t.includes("eksploitasi")) return "bg-secondary-subtle text-secondary-main border border-secondary-subtle";
    return "bg-primary-subtle text-primary-main border border-primary-subtle";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Laporan Pengaduan Masyarakat (Subdit IV Renakta Polda Jateng)" />

      {/* 1. Metric Stats Cards */}
      <div className="row row-cols-xxxl-4 row-cols-lg-4 row-cols-sm-2 row-cols-1 gy-4 mb-24">
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-1 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Total Laporan Masuk</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.total}</h4>
              </div>
              <div className="w-50-px h-50-px bg-primary-100 text-primary-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:document-text-bold" />
              </div>
            </div>
          </div>
        </div>

        <div className="col">
          <div className="card shadow-none border bg-gradient-start-2 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Laporan Hari Ini</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.todayCount}</h4>
              </div>
              <div className="w-50-px h-50-px bg-info-100 text-info-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:calendar-date-bold" />
              </div>
            </div>
          </div>
        </div>

        <div className="col">
          <div className="card shadow-none border bg-gradient-start-3 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Diverifikasi & Disetujui</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.agreed}</h4>
              </div>
              <div className="w-50-px h-50-px bg-success-100 text-success-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:check-circle-bold" />
              </div>
            </div>
          </div>
        </div>

        <div className="col">
          <div className="card shadow-none border bg-gradient-start-4 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Dengan Berkas Bukti</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.withEvidence}</h4>
              </div>
              <div className="w-50-px h-50-px bg-warning-100 text-warning-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:paperclip-bold" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Main Content Card */}
      <div className="card radius-12 border-0 shadow-sm">
        {/* Header & Filter Bar */}
        <div className="card-header py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 className="card-title text-lg text-primary-light mb-2 d-flex align-items-center gap-2">
                <Icon icon="mdi:clipboard-text-search" className="text-primary text-2xl" />
                Daftar Dokumen Pengaduan Tindak Pidana
              </h5>
              <p className="text-xs text-muted mb-0">
                Menampilkan <strong>{filteredData.length}</strong> dari total <strong>{data.length}</strong> laporan terdaftar
              </p>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2">
              <button
                onClick={getData}
                disabled={loading}
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 radius-8 px-12"
              >
                <Icon icon="solar:refresh-linear" className={loading ? "spin text-md" : "text-md"} /> Refresh
              </button>
            </div>
          </div>

          {/* Search and Filters Controls */}
          <div className="row g-3 mt-12 pt-12 border-top">
            <div className="col-lg-5 col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0 text-secondary">
                  <Icon icon="solar:magnifer-linear" className="text-lg" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0 text-sm"
                  placeholder="Cari nama pelapor, no WA, lokasi, atau kata kunci..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary border-start-0"
                    type="button"
                    onClick={() => setSearchTerm("")}
                  >
                    <Icon icon="solar:close-circle-bold" />
                  </button>
                )}
              </div>
            </div>

            <div className="col-lg-4 col-md-3 col-6">
              <select
                className="form-select text-sm"
                value={filterCrime}
                onChange={(e) => setFilterCrime(e.target.value)}
              >
                <option value="all">Semua Jenis Kasus</option>
                <option value="anak">Kekerasan Anak</option>
                <option value="kdrt">KDRT</option>
                <option value="seksual">Kejahatan Seksual</option>
                <option value="tppo">Perdagangan Orang (TPPO)</option>
                <option value="ekonomi">Eksploitasi Ekonomi</option>
                <option value="lainnya">Lainnya</option>
              </select>
            </div>

            <div className="col-lg-3 col-md-3 col-6">
              <select
                className="form-select text-sm"
                value={filterEvidence}
                onChange={(e) => setFilterEvidence(e.target.value)}
              >
                <option value="all">Semua Bukti</option>
                <option value="yes">Ada Lampiran Bukti</option>
                <option value="no">Tanpa Lampiran</option>
              </select>
            </div>
          </div>
        </div>

        {/* 3. Table Data */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table border-primary-table mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="ps-20" style={{ width: "80px" }}>No. Lap</th>
                  <th>Identitas Pelapor</th>
                  <th>Jenis Kejahatan</th>
                  <th>Kronologi & TKP</th>
                  <th>Korban</th>
                  <th>Terduga Pelaku</th>
                  <th>Lampiran Bukti</th>
                  <th>Persetujuan</th>
                  <th className="text-center pe-20" style={{ width: "160px" }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-40 text-muted">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                      Memuat data laporan pengaduan...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((report, idx) => {
                    const reportId = report.id || report.report_id;
                    return (
                      <tr key={reportId} className="hover-bg-light transition-1">
                        {/* ID & Date */}
                        <td className="ps-20">
                          <span className="badge bg-dark-subtle text-dark fw-bold px-8 py-4 radius-6 font-monospace text-xs">
                            #{reportId}
                          </span>
                          <span className="text-xs text-muted d-block mt-4" style={{ fontSize: "11px" }}>
                            {formatDate(report.createdAt)}
                          </span>
                        </td>

                        {/* Pelapor */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="w-36-px h-36-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center fw-bold text-sm flex-shrink-0"
                            >
                              {(report.reporter?.full_name || "M").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <strong className="text-primary-light d-block text-sm">
                                {report.reporter?.full_name || "Masyarakat"}
                              </strong>
                              <span className="text-xs text-muted d-flex align-items-center gap-1">
                                <Icon icon="logos:whatsapp-icon" style={{ fontSize: "11px" }} />
                                {report.reporter?.phone || report.reporter?.no_wa || "-"}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Jenis Kejahatan */}
                        <td>
                          <span className={`badge px-10 py-5 radius-6 text-xs fw-semibold ${getCrimeBadgeClass(report.report_type_of_crime)}`}>
                            {report.report_type_of_crime || "Pengaduan Umum"}
                          </span>
                        </td>

                        {/* Kronologi & TKP */}
                        <td style={{ maxWidth: "220px" }}>
                          <div
                            className="text-truncate text-sm text-dark fw-medium"
                            title={report.report_case}
                          >
                            {report.report_case || "-"}
                          </div>
                          <span className="text-xs text-muted d-flex align-items-center gap-1 mt-2 text-truncate" title={report.report_location}>
                            <Icon icon="solar:map-point-linear" className="text-danger flex-shrink-0" />
                            {report.report_location || "-"}
                          </span>
                        </td>

                        {/* Korban */}
                        <td>
                          <div className="text-sm text-dark fw-medium">
                            {report.report_victim_name || "-"}
                          </div>
                          <span className="text-xs text-muted">
                            {report.report_victim_age ? `${report.report_victim_age} thn` : ""}
                            {report.report_victim_relation ? ` • ${report.report_victim_relation}` : ""}
                          </span>
                        </td>

                        {/* Terduga Pelaku */}
                        <td style={{ maxWidth: "140px" }}>
                          <span className="text-xs text-dark text-truncate d-block" title={report.report_identity_perpetrator}>
                            {report.report_identity_perpetrator || "-"}
                          </span>
                        </td>

                        {/* Lampiran Bukti */}
                        <td>
                          {report.report_attachment ? (
                            <button
                              onClick={() => handleDownload(report.report_attachment)}
                              className="btn btn-xs btn-outline-info d-inline-flex align-items-center gap-1 radius-6"
                              title="Download berkas bukti"
                            >
                              <Icon icon="solar:paperclip-bold" className="text-sm" />
                              <span>Lihat Bukti</span>
                            </button>
                          ) : (
                            <span className="badge bg-light text-muted border text-xs px-8 py-3">
                              Tidak Ada
                            </span>
                          )}
                        </td>

                        {/* Persetujuan */}
                        <td>
                          {report.report_agreement_reporter ? (
                            <span className="badge bg-success-50 text-success-700 px-8 py-4 radius-6 text-xs d-inline-flex align-items-center gap-1">
                              <Icon icon="solar:check-circle-bold" /> Setuju
                            </span>
                          ) : (
                            <span className="badge bg-secondary-subtle text-secondary px-8 py-4 radius-6 text-xs">
                              Belum
                            </span>
                          )}
                        </td>

                        {/* Action Buttons */}
                        <td className="pe-20 text-center">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            {/* Detail Button */}
                            <button
                              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 radius-6 px-8 py-4"
                              onClick={() => setSelectedReport(report)}
                              data-bs-toggle="modal"
                              data-bs-target="#reportDetailModal"
                              title="Lihat Berkas Detail Laporan"
                            >
                              <Icon icon="solar:eye-bold" />
                            </button>

                            {/* Print PDF Button */}
                            <button
                              className="btn btn-sm btn-danger d-inline-flex align-items-center gap-1 radius-6 px-8 py-4"
                              onClick={() => handleDownloadLetter(reportId)}
                              title="Cetak Surat Tanda Terima Laporan (PDF STPL Resmi)"
                            >
                              <Icon icon="solar:printer-bold" />
                            </button>

                            {/* Delete Button */}
                            <button
                              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 radius-6 px-8 py-4"
                              onClick={() => handleDelete(reportId)}
                              title="Hapus Laporan"
                            >
                              <Icon icon="solar:trash-bin-trash-bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-40 text-muted">
                      <Icon icon="solar:document-text-outline" className="text-4xl d-block mx-auto mb-8 text-muted opacity-50" />
                      Tidak ditemukan data laporan pengaduan yang sesuai filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 4. Modal Detail Berkas Pengaduan (Full Dossier) */}
      {selectedReport && (
        <div
          className="modal fade show d-block"
          id="reportDetailModal"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div className="modal-header py-16 px-24 bg-primary text-white radius-top-16 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <Icon icon="solar:shield-check-bold" className="text-2xl text-warning" />
                  <div>
                    <h6 className="modal-title text-white fw-bold mb-0">
                      Berkas Surat Laporan Pengaduan #{selectedReport.id || selectedReport.report_id}
                    </h6>
                    <span className="text-xs text-white-50">Subdit IV Renakta Ditreskrimum Polda Jateng</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedReport(null)}
                ></button>
              </div>

              <div className="modal-body p-24">
                {/* Ringkasan Header Kasus */}
                <div className="p-16 radius-12 bg-light border mb-20">
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <span className="text-xs text-secondary d-block">Jenis Kasus / Dugaan Tindak Pidana:</span>
                      <span className={`badge px-10 py-5 radius-6 mt-1 text-sm ${getCrimeBadgeClass(selectedReport.report_type_of_crime)}`}>
                        {selectedReport.report_type_of_crime || "Pengaduan Umum"}
                      </span>
                    </div>
                    <div className="col-sm-6 text-sm-end">
                      <span className="text-xs text-secondary d-block">Waktu Laporan Diterima:</span>
                      <strong className="text-sm text-dark">{formatDate(selectedReport.createdAt)}</strong>
                    </div>
                  </div>
                </div>

                {/* Bagian 1: Data Pelapor */}
                <h6 className="text-sm fw-bold text-primary border-bottom pb-6 mb-12 d-flex align-items-center gap-1">
                  <Icon icon="solar:user-bold" /> I. DATA IDENTITAS PELAPOR
                </h6>
                <div className="row g-3 mb-20 text-sm">
                  <div className="col-sm-6">
                    <span className="text-xs text-secondary d-block">Nama Lengkap Pelapor:</span>
                    <strong className="text-dark">{selectedReport.reporter?.full_name || "-"}</strong>
                  </div>
                  <div className="col-sm-6">
                    <span className="text-xs text-secondary d-block">Nomor WhatsApp / HP:</span>
                    <strong className="text-dark">{selectedReport.reporter?.phone || selectedReport.reporter?.no_wa || "-"}</strong>
                  </div>
                  <div className="col-sm-6">
                    <span className="text-xs text-secondary d-block">Jenis Kelamin Pelapor:</span>
                    <span className="text-dark">{selectedReport.reporter?.gender || "-"}</span>
                  </div>
                  <div className="col-sm-6">
                    <span className="text-xs text-secondary d-block">Alamat Domisili:</span>
                    <span className="text-dark">{selectedReport.reporter?.address || "-"}</span>
                  </div>
                </div>

                {/* Bagian 2: Kronologi & Kejadian */}
                <h6 className="text-sm fw-bold text-primary border-bottom pb-6 mb-12 d-flex align-items-center gap-1">
                  <Icon icon="solar:danger-triangle-bold" /> II. URAIAN PERISTIWA & TEMPAT KEJADIAN (TKP)
                </h6>
                <div className="mb-20 text-sm">
                  <div className="mb-12">
                    <span className="text-xs text-secondary d-block">Waktu & Kronologi Peristiwa:</span>
                    <div className="p-12 bg-light radius-8 mt-1 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedReport.report_case || "Tidak ada detail kronologi yang disertakan."}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-secondary d-block">Lokasi Kejadian (TKP):</span>
                    <div className="p-10 bg-light radius-8 mt-1 text-dark d-flex align-items-center gap-2">
                      <Icon icon="solar:map-point-bold" className="text-danger text-lg" />
                      <strong>{selectedReport.report_location || "-"}</strong>
                    </div>
                  </div>
                </div>

                {/* Bagian 3: Data Korban & Pelaku */}
                <h6 className="text-sm fw-bold text-primary border-bottom pb-6 mb-12 d-flex align-items-center gap-1">
                  <Icon icon="solar:users-group-two-rounded-bold" /> III. DATA KORBAN & TERDUGA PELAKU
                </h6>
                <div className="row g-3 mb-20 text-sm">
                  <div className="col-sm-4">
                    <span className="text-xs text-secondary d-block">Nama Korban:</span>
                    <strong className="text-dark">{selectedReport.report_victim_name || "-"}</strong>
                  </div>
                  <div className="col-sm-4">
                    <span className="text-xs text-secondary d-block">Usia Korban:</span>
                    <span className="text-dark">{selectedReport.report_victim_age ? `${selectedReport.report_victim_age} Tahun` : "-"}</span>
                  </div>
                  <div className="col-sm-4">
                    <span className="text-xs text-secondary d-block">Hubungan dengan Pelapor:</span>
                    <span className="text-dark">{selectedReport.report_victim_relation || "-"}</span>
                  </div>
                  <div className="col-12">
                    <span className="text-xs text-secondary d-block">Identitas / Ciri Terduga Pelaku:</span>
                    <div className="p-10 bg-light radius-8 mt-1 text-dark">
                      {selectedReport.report_identity_perpetrator || "Tidak diketahui / Tidak dicantumkan"}
                    </div>
                  </div>
                </div>

                {/* Bagian 4: Lampiran Bukti */}
                <h6 className="text-sm fw-bold text-primary border-bottom pb-6 mb-12 d-flex align-items-center gap-1">
                  <Icon icon="solar:paperclip-bold" /> IV. ALAT BUKTI PENDUKUNG
                </h6>
                <div className="mb-16 text-sm">
                  {selectedReport.report_attachment ? (
                    <div className="p-14 bg-info-subtle border border-info-subtle radius-8 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <Icon icon="solar:file-check-bold" className="text-info text-2xl" />
                        <div>
                          <strong className="text-dark text-sm d-block">Tersedia Berkas Lampiran Bukti</strong>
                          <span className="text-xs text-secondary">Tipe: {selectedReport.type_attachment || "Dokumen/Media"}</span>
                        </div>
                      </div>
                      <button
                        className="btn btn-primary btn-sm px-16 radius-8 d-flex align-items-center gap-1"
                        onClick={() => handleDownload(selectedReport.report_attachment)}
                      >
                        <Icon icon="solar:download-bold" /> Unduh Berkas
                      </button>
                    </div>
                  ) : (
                    <div className="p-12 bg-light radius-8 text-secondary text-xs">
                      Pelapor tidak melampirkan file bukti pendukung digital saat pengaduan.
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer py-14 px-24 bg-light d-flex align-items-center justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-16 radius-8"
                  onClick={() => setSelectedReport(null)}
                >
                  Tutup
                </button>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-danger btn-sm px-16 radius-8 d-flex align-items-center gap-1"
                    onClick={() => handleDownloadLetter(selectedReport.id || selectedReport.report_id)}
                  >
                    <Icon icon="solar:printer-bold" /> Cetak Surat PDF (STPL)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default ReportPage;
